import { Immutable } from "immer";
import React from "react";
import { useRovingTabList } from "tsi-common-react/src/hooks/useRovingTabList";

import type { TabNav as Value } from "@reactivated";

import { AccordionSection } from "../AccordionSection";

/**
 * Faithful port of the legacy server-rendered ``cms/components/tab-nav.html``
 * (toppers ``TabNav`` block).
 *
 * Tab switching is owned here via the shared ``useRovingTabList`` hook plus
 * component state: the active tab carries ``tabs__tab--is-active`` (+
 * ``aria-selected="true"`` / roving ``tabIndex=0``) and its pane carries
 * ``tabs__pane--is-active``. Those ``--is-active`` classes are load-bearing --
 * the ``.tabs__pane`` SCSS is ``display: none`` until ``--is-active`` (and the
 * active tab flips from gray to black), so the class is what reveals a pane.
 * ``activeIndex`` starts at 0 so the FIRST tab/pane render active, keeping the
 * SSR markup identical to the hydrated DOM (the legacy ``utility_page.html``
 * on-load JS added those exact classes to the first tab/pane after load; baking
 * them into the render is hydration-stable and needs no post-load mutation).
 *
 * The anchor ``href`` still points at the pane ``id`` for a no-JS fragment
 * fallback and the ``aria-controls`` / ``aria-labelledby`` pairing; the hook's
 * click handler prevents the default fragment navigation. The ids stay the
 * legacy 1-indexed ``tab{N}`` / ``tab-content{N}`` (selector-safe, unlike
 * ``useId()``'s colon-delimited output). Two TabNavs on one page would collide
 * ids -- this is the legacy behavior, and the block cannot see its outer
 * page-body id to namespace them.
 *
 * Per the legacy template, ``block_header`` is NOT rendered (tab-nav.html ignores
 * it). ``section.title`` is a plain ``CharBlock`` -> escaped text. ``section.body``
 * is a nested ``StreamBlock``; legacy ``{{ section.body }}`` rendered it via
 * Wagtail's ``StreamBlock.render_basic``, which wraps EACH child in
 * ``<div class="w-block-{type} block-{type}">`` (verified in
 * ``wagtail/blocks/stream_block.py``). We reproduce that wrapper per child
 * (load-bearing for the screenshot gate -- ``.tabs__pane`` SCSS may target
 * ``.block-*`` children), matching the committed stearns ``TabsBlock`` idiom.
 */
type IProps = Immutable<{
    value: Value;
}>;

type TabSection = NonNullable<Value["sections"]>[number];
type TabBody = NonNullable<TabSection["body"]>;
type TabBodyItem = TabBody[number];

/**
 * Render a single nested-StreamField child the way Wagtail's
 * ``StreamBlock.render_basic`` did, wrapped in ``w-block-{type} block-{type}``.
 *
 * - ``heading`` (CharBlock) -> escaped text.
 * - ``paragraph`` (RichTextBlock) -> already-serialized HTML string, raw-injected
 *   (matches the toppers SidekickBlock/ComparisonModule direct-injection idiom;
 *   thelabui's ``<RichTextBlock>`` would add an extra wrapper div).
 * - ``image`` (ImageChooserBlock) -> ``<img>`` mirroring Wagtail's
 *   ``render_basic`` (``get_rendition_or_not_found(value, "original").img_tag()``),
 *   i.e. ``src``/``width``/``height``/``alt`` from the original image; ``alt`` is
 *   the image's ``default_alt_text``.
 * - ``html`` (RawHTMLBlock) -> raw HTML string, raw-injected.
 * - ``accordion_section`` -> the ported AccordionSection component.
 */
const renderTabBodyItem = (item: Immutable<TabBodyItem>): React.JSX.Element => {
    const className = `w-block-${item.type} block-${item.type}`;
    switch (item.type) {
        case "heading":
            return <div className={className}>{item.value}</div>;
        case "paragraph":
            return (
                <div
                    className={className}
                    dangerouslySetInnerHTML={{ __html: item.value }}
                />
            );
        case "image":
            return (
                <div className={className}>
                    <img
                        src={item.value.url}
                        alt={item.value.default_alt_text}
                        width={item.value.width}
                        height={item.value.height}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            );
        case "html":
            return (
                <div
                    className={className}
                    dangerouslySetInnerHTML={{ __html: item.value }}
                />
            );
        case "accordion_section":
            return (
                <div className={className}>
                    <AccordionSection value={item.value} />
                </div>
            );
    }
};

export const TabNav = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const sections = v.sections ?? [];
    const { activeIndex, getTabProps } = useRovingTabList({
        count: sections.length,
    });

    return (
        <div className="tabs">
            {/* NAV: loop through section titles to build the tab list. `sections`
                is a Wagtail ListBlock (no per-item id in the type) -> index key;
                the index also drives the 1-indexed `tab{N}`/`tab-content{N}` ids
                that mirror the legacy `forloop.counter`. First tab is selected. */}
            <div className="tabs__nav">
                <ul className="tabs__tab-list" role="tablist">
                    {sections.map((section, index) => {
                        const n = index + 1;
                        return (
                            <li key={index} className="tabs__tab-list-item">
                                <a
                                    href={`#tab-content${n}`}
                                    className={
                                        index === activeIndex
                                            ? "tabs__tab tabs__tab--is-active"
                                            : "tabs__tab"
                                    }
                                    id={`tab${n}`}
                                    role="tab"
                                    aria-controls={`tab-content${n}`}
                                    {...getTabProps(index)}
                                >
                                    {section.title}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* CONTENT: second loop over the same `sections` builds the panes,
                matching the legacy two-loop structure. Each pane id ties back to
                its nav anchor's `href`/`aria-controls`. */}
            <div className="tabs__content">
                {sections.map((section, index) => {
                    const n = index + 1;
                    return (
                        <div
                            key={index}
                            id={`tab-content${n}`}
                            className={
                                index === activeIndex
                                    ? "tabs__pane tabs__pane--is-active"
                                    : "tabs__pane"
                            }
                            aria-labelledby={`tab${n}`}
                            role="tabpanel"
                            tabIndex={0}
                        >
                            {(section.body ?? []).map((item) => (
                                // body StreamBlock children carry stable ids.
                                <React.Fragment key={item.id}>
                                    {renderTabBodyItem(item)}
                                </React.Fragment>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
